/**
 * پیاده‌سازیِ پورت‌های core برای مرورگر — و صداکردنِ initCore.
 * قرینه‌ی apps/mobile/src/native/bootstrap.ts؛ همون قرارداد، ابزارِ متفاوت.
 *
 * باید **اولین import** در main.tsx باشه، به همون دلیلی که در موبایل هست:
 * import‌ها قبل از بدنه‌ی ماژول اجرا می‌شن، پس اگه یه فایلِ دیگه زودتر به
 * چیزی از core برسه که فوری پورت لازم داره، initCore هنوز صدا زده نشده.
 */
import { initCore } from '@/core/platform';
import { setShareLinkBase } from '@/core/lib/shareLink';
import { setBrandName } from '@/core/lib/i18n';
import { lookupIpLocation } from '@/core/lib/ipCity';
import { setEntry, trackDomTap, trackResume, trackSessionEnd } from '@/core/lib/analytics';
import { dlog, flushDevLog, installDevLogConsole } from '@/core/lib/devlog';
import { flushDiag, startDiag } from '@/core/lib/diagLog';
import { prefLogRepo } from '@/core/data/prefLogRepo';
import { competitorRepo } from '@/core/data/competitorRepo';
import { installKeyboardInset } from '@/ui/keyboardInset';
import { applySiteIcon } from './siteIcon';
import type { DiagFile, KeyValueStore, PlatformInfo } from '@/core/ports';

/**
 * localStorage همزمانه و core رابطِ Promise می‌خواد — پس فقط دورش پیچیده می‌شه.
 * try/catch لازمه: در حالتِ ناشناسِ بعضی مرورگرها یا وقتی کاربر کوکی/ذخیره‌سازی
 * را بسته، خودِ خواندن throw می‌کنه. اون‌وقت باید مثل «چیزی ذخیره نشده» رفتار
 * کنیم، نه اینکه کلِ سایت بالا نیاد.
 */
const storage: KeyValueStore = {
  async getItem(key) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  async setItem(key, value) {
    try { window.localStorage.setItem(key, value); } catch { /* حافظه در دسترس نیست */ }
  },
  async removeItem(key) {
    try { window.localStorage.removeItem(key); } catch { /* حافظه در دسترس نیست */ }
  },
};

/**
 * لاگِ تشخیصیِ همین نشست (نگاه کن به core/lib/diagLog) — قرینه‌ی فایلِ روی
 * گوشی، این‌جا یک کلیدِ localStorage.
 *
 * چرا localStorage و نه sessionStorage: تبی که کرش کرده یا صفحه‌ای که ریلود
 * شده، دقیقاً همان چیزی است که می‌خواهیم بعدش بخوانیم — و sessionStorage
 * برای تبِ تازه خالی است. کلیدِ جدا از `whosup.prefs` است تا پاک شدنش هیچ
 * تنظیمی را نبرد.
 */
const DIAG_KEY = 'whosup.diag.log';

const diagFile: DiagFile = {
  /**
   * عمداً `try/catch` ندارد، برخلافِ `storage` بالا: اگر حافظه پر یا بسته
   * باشد این باید **بترکد** تا `diagLog` بفهمد و برای همیشه‌ی این نشست
   * دست از نوشتن بردارد. یک شکستِ بی‌صدا یعنی هر پنج ثانیه همان خطا،
   * با هزینه‌اش، بی‌آنکه کسی خبردار شود.
   */
  async write(text) {
    window.localStorage.setItem(DIAG_KEY, text);
  },
  async read() {
    try { return window.localStorage.getItem(DIAG_KEY); } catch { return null; }
  },
};

const platform: PlatformInfo = {
  os: 'web',
  // نسخه‌ی مرورگر ارزشِ تشخیصی داره (باگ‌های سافاری واقعی‌ان)، ولی رشته‌ی خامِ
  // userAgent برای لاگ زیادی طولانیه — همون تکه‌ی «موتور/نسخه» کافیه.
  osVersion: browserLabel(),
  // در زمانِ بیلد جاگذاری می‌شه؛ نگاه کن به vite.config.ts و .env.example.
  appVersion: import.meta.env.VITE_APP_VERSION ?? null,
  isWeb: true,
};

function browserLabel(): string {
  const ua = navigator.userAgent;
  const m = /(Firefox|Edg|Chrome|Version)\/(\d+)/.exec(ua);
  if (!m) return ua.slice(0, 60);
  const name = m[1] === 'Edg' ? 'Edge' : m[1] === 'Version' ? 'Safari' : m[1];
  return `${name} ${m[2]}`;
}

/**
 * حدسِ شهر از روی IP — برای بازدیدکننده‌ای که هنوز حساب نداره.
 *
 * پشتِ یه پرچمِ محیطی، و به همین دلیلِ ساده: این تنها جای اپه که به یه
 * سرویسِ **شخصِ ثالث** وصل می‌شه. توی `.env.e2e` خاموشه، پس تست‌ها هیچ‌وقت
 * به شبکه‌ی بیرون نمی‌زنن — که هم کندشون می‌کرد، هم با نرخ‌محدودیِ اون
 * سرویس (HTTP 429) به‌شکلِ خطای کنسول ظاهر می‌شد و کلِ سوییت رو قرمز می‌کرد.
 *
 * پیش‌فرض روشنه: هر جا صریحاً `0` نذاشته باشیم، بازدیدکننده شهرِ خودش رو
 * می‌بینه نه شهرِ پیش‌فرض.
 */
const ipGeoOn = (import.meta.env.VITE_IP_GEO ?? '1') !== '0';

/**
 * آیکونِ نارنجیِ تب اگر این نسخه پروداکشن نباشد — نگاه کن به `./siteIcon`.
 *
 * زودترین کارِ این فایل است چون تنها چیزی که لازم دارد `<head>` است و هرچه
 * دیرتر انجام شود، آیکونِ اشتباه بیشتر دیده می‌شود.
 */
applySiteIcon();

/**
 * وقتی تب پنهان می‌شود، هرچه در صفِ آمار مانده همان‌جا می‌رود.
 *
 * `visibilitychange` و نه `beforeunload`: دومی روی موبایل قابل‌اعتماد نیست
 * و مرورگرها به‌مرور بی‌اثرش کرده‌اند. بدونِ این، رویدادهای آخرِ هر بازدید
 * معمولاً گم می‌شدند — و همان‌ها جالب‌ترین‌اند.
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    // ⚠ **قبل از** خالی کردنِ صف: `trackSessionEnd` خودش یک ردیف می‌سازد
    // (کجا رهایش کرد و چقدر آن‌جا ماند) و آن ردیف باید در همین دسته برود،
    // وگرنه تا بازدیدِ بعدی — که شاید هیچ‌وقت نیاید — روی هوا می‌ماند.
    trackSessionEnd('hidden');
    flushDevLog(); flushDiag();
  } else if (document.visibilityState === 'visible') {
    // برگشت به تب. بدونِ این، مدتی که تب پشتِ سر بود به‌عنوانِ «مدتِ ماندن
    // روی این صفحه» شمرده می‌شد.
    trackResume();
  }
});

/**
 * «از کجا آمد» — یک بار، پیش از هر رویدادِ دیگری.
 *
 * `document.referrer` فقط همین‌جا خوانده می‌شود و فقط **میزبانش** نگه
 * داشته می‌شود؛ مسیرِ صفحه‌ی ارجاع‌دهنده می‌تواند خودش داده باشد. تفکیکش
 * در `core/lib/journeyLogic.classifyEntry` است و همان‌جا آزموده می‌شود.
 */
setEntry(window.location.href, document.referrer);

/**
 * هر کلیکی روی هر دکمه‌ای — حتی آن‌هایی که از کیت نیستند.
 *
 * کیت خودش لمس‌هایش را ثبت می‌کند (`tapped()` در core/lib/devlog)، ولی ۱۰۴
 * دکمه‌ی این اپ از کیت نمی‌آیند — از جمله چیپ‌های دسته‌بندیِ صفحه‌ی کشف که
 * پرکارترین‌اند. این شنونده همان‌ها را می‌گیرد؛ `trackDomTap` خودش جلوی
 * ثبتِ دوباره‌ی دکمه‌های کیت را می‌گیرد.
 *
 * ⚠ فازِ **capture**: دکمه‌ای که خودش صفحه را عوض می‌کند ممکن است پیش از
 * رسیدنِ رویداد به `document` از درخت برداشته شود.
 *
 * ⚠ **متنِ دکمه ذخیره نمی‌شود.** `analyticsLabel` (در core) کلیدِ ترجمه‌اش
 * را می‌گذارد و هر چیزِ دیگری — اسمِ یک تگ، اسمِ یک آدم — `other` می‌شود.
 * برای دکمه‌ای که متنش از دیتابیس می‌آید و باز هم باید شمرده شود،
 * `data-tap="نامِ ثابت"` روی خودِ عنصر بگذار.
 */
document.addEventListener('click', (e) => {
  const el = (e.target as Element | null)?.closest?.('button, a, [role="button"]');
  if (!el) return;
  const kind = el.classList.contains('chip') ? 'chip'
    : el.tagName === 'A' ? 'link' : '';
  const marked = el.getAttribute('data-tap');
  const text = (marked ?? el.textContent ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return;
  // ⚠ فقط `data-tap` «معتمد» است. متنِ خودِ دکمه می‌تواند اسمِ یک تگ یا یک
  // آدم باشد، و آن‌طرف فقط اگر کلیدِ ترجمه باشد نگه داشته می‌شود.
  trackDomTap(kind ? `${kind}:${text}` : text, marked !== null);
}, true);

/**
 * لاگِ فعالیتِ توسعه — قرینه‌ی همین سه خط در apps/mobile/app/_layout.tsx.
 * نگاه کن به core/lib/devlog؛ تا وقتی حسابِ صاحبِ لاگ وارد نشده هیچ‌کدام
 * این‌ها به شبکه نمی‌زنند.
 */
installDevLogConsole();

/**
 * قولی که مرورگر داده و کسی نگرفتش.
 *
 * روی موبایل `installGlobalErrorReporter` این کار را با `ErrorUtils` می‌کند
 * که در مرورگر وجود ندارد؛ معادلِ مرورگری‌اش همین دو رویداد است. بدونشان
 * یک `await`ِ شکسته در یک هندلر هیچ ردی نمی‌گذارد جز یک خطای قرمز در
 * کنسولی که هیچ‌کس بازش نکرده.
 */
window.addEventListener('unhandledrejection', (e) => {
  const r = (e as PromiseRejectionEvent).reason;
  dlog('error', 'unhandledrejection', {
    ok: false, detail: { message: r instanceof Error ? r.message : String(r) },
  });
});
window.addEventListener('error', (e) => {
  dlog('error', 'window.error', {
    ok: false, detail: { message: e.message, at: `${e.filename}:${e.lineno}` },
  });
});

/**
 * لینک‌های /s/ و /i/ که این‌جا ساخته می‌شن باید به **همین** سایت اشاره کنن:
 * روی پروداکشن origin همون outami.com است (هیچ تغییری)، ولی روی استیجینگ
 * لینکِ outami.com به دیتابیسِ پروداکشن می‌خورد که سیگنالِ استیجینگ رو
 * ندارد — «لینک منقضی شده» برای لینکی که همین الان ساخته شده (WHOSUP-117).
 */
setShareLinkBase(window.location.origin);

/**
 * نامِ برند — همان الگوی بالا: چیزی که در زمانِ اجرا معلوم می‌شود، نه در کد.
 *
 * `VITE_BRAND_NAME` را بیلد می‌دهد (متغیرِ محیطیِ پروژه‌ی Pages). نبودنش یعنی
 * برندِ پیش‌فرض، پس `npm run web` و بیلدِ امروز هیچ فرقی نمی‌کنند.
 *
 * این فقط متنِ داخلِ اپ را عوض می‌کند. عنوانِ تب و تگ‌های og و آیکون‌ها در
 * index.html ثابت‌اند و جاوااسکریپت به آن‌ها نمی‌رسد — آن‌ها را
 * scripts/brand-apply.mjs موقعِ بیلد می‌نویسد.
 */
setBrandName(import.meta.env.VITE_BRAND_NAME);

initCore({
  storage,
  platform,
  diagFile,
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? '',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
  ...(ipGeoOn ? { ipLocation: lookupIpLocation } : {}),
  // imageCache عمداً داده نشده: خودِ مرورگر عکس‌ها را با هدرهای HTTP کش می‌کنه.
});

/**
 * لاگِ تشخیصیِ روی همین مرورگر — قرینه‌ی همین خط در apps/mobile/app/_layout.
 * **بعد از** initCore، چون به پورتِ بالا و به `storage` نیاز دارد. هیچ‌وقت
 * خودبه‌خود به شبکه نمی‌زند؛ نگاه کن به core/lib/diagLog.
 */
startDiag();

/**
 * ثبتِ انتخاب‌های کاربر (مایگریشن ۰۱۱۱).
 *
 * **بعد از** `initCore` چون کلاینتِ Supabase را لازم دارد، و قرینه‌اش در
 * `apps/mobile/src/native/bootstrap.ts` است. از این لحظه هر `setTheme` /
 * `setPref` / `setLocale` خودش ثبت می‌شود و هیچ صفحه‌ای لازم نیست کاری بکند.
 *
 * تا وقتی کسی وارد نشده هیچ‌چیز به شبکه نمی‌زند — `prefLogRepo` بدونِ
 * شناسه‌ی کاربر بی‌صدا رد می‌شود، و `SessionProvider` شناسه را می‌دهد.
 */
prefLogRepo.install();

/**
 * فهرستِ سایت‌های رقیب برای نشانِ لینک‌های خروجی (مایگریشن ۰۱۳۴) — قرینه‌ی
 * همین خط در `apps/mobile/src/native/bootstrap.ts`. یک کوئریِ چندسطریِ
 * بدونِ ورود؛ اگر نرسد، فهرستِ سفت‌شده‌ی `core/lib/outboundUrl` کار می‌کند
 * و هیچ لینکی منتظرِ جوابش نمی‌ماند.
 */
competitorRepo.refresh();

/**
 * قدِ کیبوردِ گوشی، به‌صورتِ یک متغیرِ CSS — تا مودالِ `position: fixed` روی
 * کیبورد نیفتد. نگاه کن به `ui/keyboardInset`؛ قرینه‌اش در موبایل
 * `src/ui/keyboard.tsx` است.
 *
 * جایش این‌جاست و نه داخلِ یک کامپوننت: به هیچ صفحه و هیچ نشستی وابسته
 * نیست و باید پیش از اولین فوکوسِ کاربر برقرار باشد.
 */
installKeyboardInset();
